/**
 * @module builder
 */
import type {
  AreaSubstrate,
  OptionsDocument,
  QuoteDocument,
  QuoteProduct,
  TieredPackageProductRule,
  TieredPackages,
  TieredPackageTierId
} from 'paintscout'
import type { ProductSwap } from '../util/swap-quote-products/swap-quote-products'
import { swapQuoteProducts } from '../util/swap-quote-products/swap-quote-products'
import { isAllProductsRateSection, isLineItemsRateSection } from './tiered-package-product-rules'
// Deep import rather than the '../util' barrel, which re-exports calculate.ts and closes a cycle
// back into builder — the same hazard get-tier-prices documents.
import { calculateQuote } from '../util/calculate/calculate-quote'

/**
 * Resolve which product a rule puts on a given tier.
 *
 * A tier with no entry keeps the base product, which is what makes a partly-filled rule safe: set
 * only an upgrade and the other tiers still price and read correctly.
 */
export function getRuleProductForTier(rule: TieredPackageProductRule, tierId: TieredPackageTierId): string {
  return rule.products?.[tierId] || rule.baseProductKey
}

/**
 * Turn the rule list into the product swaps needed to move an estimate from one tier to another.
 *
 * Both ends resolve from the rules, so this works in either direction and needs no record of what
 * the estimate looked like before. Going Best → Good is the same lookup as Good → Best, reversed.
 */
export function getTierProductSwaps(args: {
  productRules?: TieredPackageProductRule[]
  fromTierId: TieredPackageTierId
  toTierId: TieredPackageTierId
}): ProductSwap[] {
  const { productRules, fromTierId, toTierId } = args

  if (!productRules?.length || fromTierId === toTierId) {
    return []
  }

  return productRules.reduce((acc: ProductSwap[], rule) => {
    if (!rule?.baseProductKey || !rule.rateSection) {
      return acc
    }

    const fromProductKey = getRuleProductForTier(rule, fromTierId)
    const toProductKey = getRuleProductForTier(rule, toTierId)

    if (fromProductKey === toProductKey) {
      return acc
    }

    const swap: ProductSwap = { fromProductKey, toProductKey }

    if (isAllProductsRateSection(rule.rateSection)) {
      // Written against the product itself: no target at all, so it swaps wherever the product
      // appears — every rate section and every line item.
    } else if (isLineItemsRateSection(rule.rateSection)) {
      // "Line Items" is a rate category in the UI but not a real rate section, so it scopes to line
      // items and to no area substrate.
      swap.target = { lineItems: true }
    } else {
      swap.target = { sections: [rule.rateSection] }
    }

    acc.push(swap)

    return acc
  }, [])
}

/**
 * Whether moving to `tierId` changes any product relative to the base tier.
 *
 * Drives the pricing decision in `tierAdjustment`: a tier that swaps products is priced by the
 * swap, so its percentage/dollar adjustment must not also apply.
 */
export function hasTierProductSwaps(args: { tieredPackages?: TieredPackages; tierId: TieredPackageTierId }): boolean {
  const { tieredPackages, tierId } = args

  if (!tieredPackages?.productRules?.length) {
    return false
  }

  return (
    getTierProductSwaps({
      productRules: tieredPackages.productRules,
      fromTierId: tieredPackages.baseTierId,
      toTierId: tierId
    }).length > 0
  )
}

/**
 * Whether every product named by the rules exists in this options document.
 *
 * The customer view receives a cut-down product library (filterOptionsDetails), so a swap there can
 * resolve nothing and silently no-op. Callers that would write prices back onto the quote must check
 * this first, or a customer-side recalculation would overwrite the estimator's correct numbers with
 * numbers computed from missing products.
 */
export function canResolveTierProducts(args: { tieredPackages?: TieredPackages; options: OptionsDocument }): boolean {
  const { tieredPackages, options } = args

  const rules = tieredPackages?.productRules
  if (!rules?.length) {
    return false
  }

  const values = options?.options?.products?.values ?? {}

  return rules.every((rule) => {
    const keys = [rule?.baseProductKey, ...Object.values(rule?.products ?? {})].filter(Boolean)
    return keys.every((key) => !!values[key])
  })
}

type CoverageSnapshot = QuoteProduct['tierBaseCoverage']

function readCoverage(quoteProduct: QuoteProduct): CoverageSnapshot {
  return {
    coverage_rate_value: quoteProduct.coverage_rate_value,
    lnft_coverage_rate_value: quoteProduct.lnft_coverage_rate_value,
    quantity_coverage_rate_value: quoteProduct.quantity_coverage_rate_value
  }
}

/**
 * Walk every product slot of every substrate in the given rate sections, in a stable order.
 * `sections` of null means every section — a rule written against the product itself is unscoped.
 */
function eachScopedProduct(
  quote: QuoteDocument,
  sections: string[] | null,
  visit: (quoteProduct: QuoteProduct, slotKey: string) => void
) {
  Object.keys(quote.areas ?? {}).forEach((areaKey) => {
    const area = quote.areas[areaKey]

    Object.keys(area?.substrates ?? {}).forEach((section) => {
      if (sections && !sections.includes(section)) {
        return
      }

      ;(area.substrates[section] ?? []).forEach((substrate: AreaSubstrate, substrateIndex: number) => {
        const base = `${areaKey}|${section}|${substrate?.key ?? substrateIndex}`

        if (substrate?.product) {
          visit(substrate.product, `${base}|legacy`)
        }
        ;(substrate?.products ?? []).forEach((quoteProduct, index) => visit(quoteProduct, `${base}|${index}`))
      })
    })
  })
}

/**
 * Carry the base tier's coverage across a swap, and restore it on the way back.
 *
 * A rate can override the coverage of its own default product, and that override is baked into the
 * substrate's coverage values when it is created. The rate snapshot stored on the substrate omits
 * `default_products`, so once the swap rebuilds the product from the library the override cannot be
 * looked up again — half the rates in the seed data set one, and without this the estimate returns
 * from a tier at a different volume and a different price.
 *
 * So the outgoing base coverage rides along on the product as `tierBaseCoverage`, and is applied
 * again the moment the slot returns to a base product.
 */
function carryBaseCoverage(args: {
  before: QuoteDocument
  after: QuoteDocument
  sections: string[] | null
  baseProductKeys: string[]
}): QuoteDocument {
  const { before, after, sections, baseProductKeys } = args

  const previous = new Map<string, { productKey?: string; coverage: CoverageSnapshot; carried?: CoverageSnapshot }>()

  eachScopedProduct(before, sections, (quoteProduct, slotKey) => {
    previous.set(slotKey, {
      productKey: quoteProduct?.product?.name,
      coverage: readCoverage(quoteProduct),
      carried: quoteProduct.tierBaseCoverage
    })
  })

  eachScopedProduct(after, sections, (quoteProduct, slotKey) => {
    const outgoing = previous.get(slotKey)
    if (!outgoing) {
      return
    }

    const wasBase = baseProductKeys.includes(outgoing.productKey)
    const isBase = baseProductKeys.includes(quoteProduct?.product?.name)

    if (isBase) {
      // Back on a base product: restore whatever coverage it had, then drop the note.
      const restore = wasBase ? outgoing.coverage : outgoing.carried
      if (restore) {
        Object.assign(quoteProduct, restore)
      }
      delete quoteProduct.tierBaseCoverage
      return
    }

    // Leaving the base product, or moving between two non-base tiers — keep the note alive.
    const carried = wasBase ? outgoing.coverage : outgoing.carried
    if (carried) {
      quoteProduct.tierBaseCoverage = carried
    }
  })

  return after
}

/**
 * Apply a tier's product substitutions to the quote.
 *
 * Delegates to swapQuoteProducts, which deep-clones the quote and keeps each product's volume and
 * volume overrides while taking the new product's coverage and price. The rate's own coverage
 * override is then restored, so switching tiers and switching back returns the original price.
 */
export function applyTierProducts(args: {
  quote: QuoteDocument
  options: OptionsDocument
  fromTierId: TieredPackageTierId
  toTierId: TieredPackageTierId
  calculate?: boolean
}): QuoteDocument {
  const { quote, options, fromTierId, toTierId, calculate = true } = args

  const swaps = getTierProductSwaps({
    productRules: quote.tieredPackages?.productRules,
    fromTierId,
    toTierId
  })

  if (!swaps.length) {
    return quote
  }

  // Swap without calculating, carry the base coverage across, then calculate once over the result.
  const swapped = swapQuoteProducts({ quote, options, swaps, calculate: false })
  // An unscoped swap (a rule written against the product itself) reaches every section, so the
  // coverage carry has to look everywhere too.
  const sections = swaps.some((swap) => !swap.target)
    ? null
    : swaps.reduce((acc: string[], swap) => [...acc, ...(swap.target?.sections ?? [])], [])
  const baseProductKeys = (quote.tieredPackages?.productRules ?? []).map((rule) => rule.baseProductKey)

  const reconciled = carryBaseCoverage({ before: quote, after: swapped, sections, baseProductKeys })

  return calculate ? calculateQuote({ quote: reconciled, options }) : reconciled
}
